// eslint-disable-next-line simple-import-sort/imports
import { useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useParams, useSearchParams } from 'react-router-dom'
import useDirectory from 'hooks/useDirectory'
import { Building } from 'models/Building'
import { useRecoilState, useRecoilValue } from 'recoil'
import { listPointState } from 'store/buildingStore'
import { currentAnnotation } from 'store/krpanoStore'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import { FloorMapElement } from 'types/building'

import Spinner from 'components/atoms/Spinner'

import AnnotationModal from '../buildingDetailViewMode/components/AnnotationModal/AnnotationModal'

import BusinessInfoModal from './components/BusinessInfoModal'
import TourViewerFloorMap from './components/map/TourViewerFloorMap'
import ThumbnailCarousel from './components/ThumbnailCarousel/ThumbnailCarousel'
import TourViewerPanorama from './components/TourViewerPanorama'
import MapLocationModal from './components/map/MapLocationModal'

export default function TourViewPage() {
  const prams = useParams()
  const { getBuiildingType } = useDirectory()
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [searchParams] = useSearchParams()
  const floorId = searchParams.get('floor')
  const areaId = searchParams.get('area')
  const pointId = searchParams.get('point')
  const [selectedPointID, setSelectedPointID] = useState<string>('')
  const [, setPoints] = useRecoilState(listPointState)
  const [openBusinessInfo, setOpenBusinessInfo] = useState(false)
  const [openMapLocation, setOpenMapLocation] = useState(false)
  const currentSelected = useRecoilValue(currentAnnotation)
  const [logoSize, setLogoSize] = useState({ width: 0, height: 0 })
  const matches = useMediaQuery({ query: '(min-width: 768px)' })
  const { data: building } = useSWR<Building>({
    url: `/v1/buildings/by-type?id=${prams?.id}&building_type=${getBuiildingType()}`
  })
  const { data: buildingInfo } = useSWR<Building>({
    url: `v1/buildings/${prams?.id}`
  })
  // get points for detail
  const { data, isValidating } = useSWR<FloorMapElement>(
    currentViewer?.selectedFloor
      ? {
          url: 'v1/points',
          params: {
            buildingId: prams?.id,
            floorId: currentViewer?.selectedFloor
          }
        }
      : null
  )
  // set default selected Point
  useEffect(() => {
    if (!isValidating && building) {
      if (floorId && areaId && pointId && !currentViewer.isClickToNextFloor) {
        setCurrentViewer((prev) => ({
          ...prev,
          selectedFloor: +floorId,
          selectedArea: areaId,
          selectedPoint: pointId
        }))
        return
      }
      setCurrentViewer((prev) => {
        if (prev.selectedFloor !== null) {
          const isFloorCurrent = building.floors.findIndex(
            (item) => item?.id === prev.selectedFloor
          )
          if (isFloorCurrent !== -1) {
            return prev
          }
        }
        const storedFloor = localStorage.getItem('floor')
        if (storedFloor) localStorage.removeItem('floor')
        const defaultFloor = storedFloor ? +storedFloor : building.defaultFloor

        return {
          ...prev,
          selectedFloor: defaultFloor || building.floors[0]?.id
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaId, building, floorId, isValidating, pointId, setCurrentViewer])

  useEffect(() => {
    if (data) {
      setPoints(data.points)

      // prevent select default selected point when navigate
      if (currentViewer.selectedPoint) {
        const initialIndex = data.points.findIndex(
          (item) => item.id === currentViewer.selectedPoint
        )

        if (initialIndex !== -1) {
          setSelectedPointID(currentViewer.selectedPoint || data.points[0].id)
        }

        setCurrentViewer((prev) => ({
          ...prev,
          selectedArea: `${data.points.find(
            (item) => item?.id === currentViewer.selectedPoint
          )?.areaId}`
        }))
      } else {
        // Find default point first
        const defaultPointIndex = data.points
          .filter((item) => item.image360)
          .reduce(
            (acc, curr, index, arr) =>
              curr.order < arr[acc].order ? index : acc,
            0
          )
        const defaultPoint = data.points.filter((item) => item.image360)[
          defaultPointIndex
        ]

        if (defaultPoint) {
          // Use default point instead of first point

          setSelectedPointID(defaultPoint?.id)
          setCurrentViewer((prev) => {
            return {
              ...prev,
              selectedArea: `${defaultPoint?.areaId}`,
              selectedPoint: defaultPoint?.id
            }
          })
        } else {
          // Fallback to first point if no default point found
          setSelectedPointID(data.points[0]?.id)
          setCurrentViewer((prev) => {
            return {
              ...prev,
              selectedPoint: data.points[0]?.id
            }
          })
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, currentViewer.selectedPoint])

  // Listen for manually set points from handleRedirect
  useEffect(() => {
    const handleSelectedPointIDUpdate = (pointId: string) => {
      setSelectedPointID(pointId)
    }

    ;(window as any).onSelectedPointIDUpdate = handleSelectedPointIDUpdate

    return () => {
      delete (window as any).onPointManuallySet
      delete (window as any).onSelectedPointIDUpdate
    }
  }, [])

  // Sync selectedPointID with currentViewer.selectedPoint
  useEffect(() => {
    if (
      currentViewer.selectedPoint &&
      currentViewer.selectedPoint !== selectedPointID
    ) {
      setSelectedPointID(currentViewer.selectedPoint)
    }
  }, [currentViewer.selectedPoint, selectedPointID])

  return (
    <Spinner spinning={isValidating}>
      <TourViewerFloorMap
        setSelectedPointID={setSelectedPointID}
        floors={building?.floors}
      />

      {buildingInfo?.extraInfo?.logoUrl && (
        <div
          onClick={() => {
            window.open(buildingInfo?.extraInfo.logoLink, '_blank')
          }}
          className="absolute top-0 left-0 z-[98]"
        >
          <img
            className="object-fit bg-transparent"
            style={{
              width: logoSize.width,
              height: logoSize.height
            }}
            src={buildingInfo.extraInfo.logoUrl}
            alt="logo"
            loading="lazy"
            onLoad={(e) => {
              const img = e.target as HTMLImageElement
              const isLandscape = img.naturalWidth > img.naturalHeight

              setLogoSize({
                width: isLandscape
                  ? window.innerWidth * 0.25
                  : window.innerWidth * 0.15,
                height: isLandscape
                  ? window.innerWidth * 0.15
                  : window.innerWidth * 0.25
              })
            }}
          />
        </div>
      )}
      <div className="h-screen flex flex-col ">
        {currentViewer.selectedPoint ? <TourViewerPanorama /> : null}

        <div className="fixed bottom-0 left-0 right-0 z-10">
          <ThumbnailCarousel
            floors={building?.floors}
            building={building}
            setSelectedPointID={setSelectedPointID}
            setOpenBusinessInfo={setOpenBusinessInfo}
            setOpenMapLocation={setOpenMapLocation}
          />
        </div>
      </div>
      {openBusinessInfo && <BusinessInfoModal setOpen={setOpenBusinessInfo} />}
      {openMapLocation && (
        <MapLocationModal setOpen={setOpenMapLocation} matches={matches} />
      )}
      {currentSelected && <AnnotationModal />}
    </Spinner>
  )
}
